import { Box, Button } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import SingleClickWaitingButton from "./SingleClickWaitingButton";
import { useActionPath } from "../routes/ApplicationUpdate";
import { useMemo } from "react";
import { useNavigate, useNavigation } from "react-router-dom";

type FormButtonsProps = {
  actionPath?: ReturnType<typeof useActionPath>,
  form: UseFormReturn<any>,
  readonly?: boolean,
  showHomeButton?: boolean,
  boxProps?: Partial<React.ComponentProps<typeof Box>>,
  nextButtonProps?: Partial<React.ComponentProps<typeof SingleClickWaitingButton>>,
};

const FormButtons: React.FC<FormButtonsProps> = ({ actionPath, form, readonly = false, showHomeButton = true, boxProps, nextButtonProps }) => {
  const navigate = useNavigate()
  // DBS-360: the page's save (its route action) runs after the button's own check, so the button stays off until the
  // save has finished; a second tap on a slow phone would otherwise send it again (a second submission is refused)
  const saving = useNavigation().state !== 'idle'

  const nextButtonTitle = useMemo(() => {
    switch (actionPath?.key) {
      case 'homepage': return 'Save & Return Home';
      case 'identity': return 'Save & Go to Identity';
      case 'names': return 'Save & Go to Names';
      case 'birth': return 'Save & Go to Birth';
      case 'addresses': return 'Save & Go to Addresses';
      case 'documents': return 'Save & Go to Documents';
      case 'submit': return 'Save & Return to Submit';
      default: return 'Continue >';
    }
  }, [actionPath])

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" {...boxProps}>
      <Button
        data-testid="back-button"
        variant="contained"
        color={form.formState.isDirty ? 'error' : 'primary'}
        onClick={() => {
          if (form.formState.isDirty && !window.confirm("Are you sure you want to go back? You will lose any unsaved changes. Click cancel to stay on this screen.")) {
            return;
          }

          window.history.back();
        }}
        sx={{ mt: 3, mb: 2, whiteSpace: 'nowrap', pl: 3, pr: 3 }}
      >
        &lt; Back
      </Button>
      {showHomeButton && (
        <Button
          data-testid="home-button"
          variant="contained"
          color={form.formState.isDirty ? 'error' : 'primary'}
          onClick={() => {
            if (form.formState.isDirty && !window.confirm("Are you sure you want to go back? You will lose any unsaved changes. Click cancel to stay on this screen.")) {
              return;
            }

            navigate('/')
          }}
          sx={{ mt: 3, mb: 2, ml: 1 }}
        >
          🏠
        </Button>)
      }
      {!readonly && (
        <SingleClickWaitingButton
          data-testid="next-button"
          type="submit"
          variant="contained"
          color={form.formState.isValid ? 'primary' : 'inherit'}
          onClick={async event => {
            const isValid = await form.trigger();
            if (!isValid) {
              console.warn("Form is not valid", form.formState.errors);
              event.preventDefault();
            }
          }}
          fullWidth
          sx={{ mt: 3, mb: 2, ml: 1 }}
          {...nextButtonProps}
          disabled={saving || nextButtonProps?.disabled}
        >
          {nextButtonTitle}
        </SingleClickWaitingButton>
      )}
    </Box>
  );
}

export default FormButtons;
